import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { SocialLogin } from "@/components/social-login";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { signup } from "@/src/services/auth_service";
import { captureEvent } from "@/src/lib/posthog";
import { signupSchema, type SignupValues } from "./_schemas/signup";

const CALENDLY_URL = "https://calendly.com/sanctions-io/30min";

export default function SignupPage() {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  async function handleValid(values: SignupValues) {
    setError(null);
    const recaptchaToken = executeRecaptcha ? await executeRecaptcha("signup") : undefined;
    // Single-step contract signup: name + email + password go in one call. The
    // backend creates the account and issues NO token (the user signs in next).
    const res = await signup(
      {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password1: values.password,
        password2: values.confirm,
      },
      recaptchaToken,
    );
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    captureEvent("Registered", { email_domain: values.email.split("@")[1] });
    setSubmitted(true);
  }

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <MarketingPanel />

      <div className="flex flex-1 items-center justify-center bg-grey-25 px-6 py-12 md:basis-2/5 md:px-8 lg:px-12">
        {submitted ? (
          <div className="popup w-full max-w-100 rounded-lg">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <h1 className="text-h4 text-black">Account created</h1>
                <p className="text-small text-grey-900">
                  You&apos;re all set. If we need to verify your email, we&apos;ve sent you a link -
                  then sign in to get started.
                </p>
              </div>
              <Button
                hierarchy="primary"
                size="md"
                className="w-full"
                onClick={() => navigate("/login")}
                rightIcon={<Icon name="arrow_forward" size={20} />}
              >
                Go to login
              </Button>
            </div>
          </div>
        ) : (
          <div className="popup w-full max-w-100 rounded-lg">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <h1 className="text-h4 text-black">Welcome to sanctions.io</h1>
                <p className="text-small text-grey-900">Enter your details to sign up.</p>
              </div>

              <Form
                schema={signupSchema}
                defaultValues={{
                  firstName: "",
                  lastName: "",
                  email: "",
                  password: "",
                  confirm: "",
                }}
                onSubmit={handleValid}
                className="flex flex-col gap-4"
              >
                <div className="flex gap-4">
                  <div className="flex min-w-0 flex-1">
                    <FormField
                      name="firstName"
                      label="First name"
                      autoComplete="given-name"
                      placeholder="Jonathan"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1">
                    <FormField
                      name="lastName"
                      label="Last name"
                      autoComplete="family-name"
                      placeholder="Lopez"
                    />
                  </div>
                </div>

                <FormField
                  name="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  placeholder="username@example.com"
                />

                <FormField
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••••"
                  rightIcon={
                    <PwToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                  }
                />
                <FormField
                  name="confirm"
                  label="Confirm password"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••••"
                  rightIcon={
                    <PwToggle shown={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
                  }
                />

                {error && (
                  <div
                    role="alert"
                    className="rounded-sm border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
                  >
                    {error}
                  </div>
                )}

                <FormSubmit
                  hierarchy="primary"
                  size="md"
                  className="w-full"
                  pendingLabel="Creating account…"
                  rightIcon={<Icon name="arrow_forward" size={20} />}
                >
                  Sign up
                </FormSubmit>

                <Divider label="or" />

                <SocialLogin action="Sign up with" />
              </Form>

              <div className="flex items-center justify-center gap-1.5">
                <p className="text-small text-grey-900">Already have an account?</p>
                <Link
                  to="/login"
                  className="text-small font-medium text-green-700 hover:text-green-600"
                >
                  Log in
                </Link>
              </div>

              <p className="text-center text-small text-grey-500">
                This site is protected by reCAPTCHA and the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-grey-700"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-grey-700"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketingPanel() {
  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-black px-8 py-10 text-white md:basis-3/5 md:px-12 lg:px-16 lg:py-14">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-green-500/12 to-transparent"
      />

      <div className="relative flex w-full flex-1 flex-col gap-12">
        <img
          src="/brand/logo-white.svg"
          alt="sanctions.io"
          className="h-8 w-43.5"
          draggable={false}
        />

        <div className="flex max-w-160 flex-col gap-8">
          <div className="flex flex-col gap-4">
            <p className="text-tiny font-bold tracking-[0.12em] text-green-400 uppercase">
              Free for 7 days
            </p>
            <h2 className="text-h2 text-white md:text-h1">
              Sanctions and AML compliance - made simple.
            </h2>
            <p className="text-body text-grey-300">
              Create an account and start screening against consolidated sanctions, PEP and
              adverse-media data in under a minute. No card, no sales call.
            </p>
          </div>

          <ul aria-label="What's included" className="flex flex-col border-t border-grey-900/50">
            {FEATURES.map((feature) => (
              <li key={feature.title} className="flex gap-4 border-b border-grey-900/50 py-5">
                <Icon name={feature.icon} size={20} className="mt-0.5 shrink-0 text-green-400" />
                <div className="flex flex-col gap-1">
                  <p className="text-small font-semibold text-white">{feature.title}</p>
                  <p className="text-small text-grey-300">{feature.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto flex flex-col gap-6 md:flex-row md:items-center md:gap-10">
          <p className="text-tiny font-bold tracking-[0.12em] text-white uppercase md:max-w-30">
            Trusted by 400+ customers
          </p>
          <ul aria-label="Customers" className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {CUSTOMERS.map((customer) => (
              <li key={customer} className="leading-tight font-bold text-grey-500 first:max-w-32">
                {customer}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const FEATURES: { icon: string; title: string; body: ReactNode }[] = [
  {
    icon: "search",
    title: "Screening and ongoing monitoring",
    body: "Screen a name once, or keep it under continuous monitoring and get alerted when a list changes.",
  },
  {
    icon: "code",
    title: "Full API access",
    body: "The same data behind one REST endpoint. Keys are live the moment you sign up.",
  },
  {
    icon: "account_circle",
    title: "A named person to ask",
    body: (
      <>
        Customer success is included in the trial -{" "}
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-green-400 hover:text-green-300"
        >
          book a call
        </a>{" "}
        whenever it helps.
      </>
    ),
  },
];

const CUSTOMERS = [
  "GLOBAL FUND FOR WOMEN",
  "Together Gaming",
  "Accelerant",
  "Loadsure",
  "PayPoint",
];

function PwToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? "Hide password" : "Show password"}
      className="flex"
    >
      <Icon name={shown ? "visibility_off" : "visibility"} size={20} />
    </button>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <div className="h-px flex-1 bg-grey-200" />
      <span className="text-small text-grey-900">{label}</span>
      <div className="h-px flex-1 bg-grey-200" />
    </div>
  );
}
